/* RGM2 product tabs. Upgrades the anchor tab bar in rgm2-product-info to ARIA tabs.
   Without this script the Description panel shows and the other tabs work as anchor links.
   When the tab bar is wider than its box, data-fade on the tab list drives an edge fade (CSS). */
if (!customElements.get('rgm2-tabs')) {
  class Rgm2Tabs extends HTMLElement {
    connectedCallback() {
      if (!this.tabs) this.init();
      if (!this.tabs || !this.tabs.length) return;
      this.listen();
    }

    disconnectedCallback() {
      if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
      if (this.fadeObserver) this.fadeObserver.disconnect();
      if (this.onFadeResize) window.removeEventListener('resize', this.onFadeResize);
    }

    init() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(index, false, true);
        });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.onHashChange = () => {
        const index = this.indexFromHash();
        if (index > -1) {
          this.select(index, false, false);
          this.revealInList(this.tabs[index]);
        }
      };

      this.queueFade = () => {
        if (this.fadeFrame) return;
        this.fadeFrame = requestAnimationFrame(() => {
          this.fadeFrame = null;
          this.updateFade();
        });
      };
      if (this.tablist) this.tablist.addEventListener('scroll', this.queueFade, { passive: true });

      const start = this.indexFromHash();
      const index = start > -1 ? start : 0;
      this.select(index, false, false);
      this.setAttribute('data-ready', '');
      if (index > 0) this.revealInList(this.tabs[index]);
    }

    listen() {
      window.addEventListener('hashchange', this.onHashChange);
      if (!this.tablist) return;
      if ('ResizeObserver' in window) {
        if (!this.fadeObserver) this.fadeObserver = new ResizeObserver(this.queueFade);
        // The tabs are observed too, so a late web font that widens the labels updates the fade.
        this.fadeObserver.observe(this.tablist);
        this.tabs.forEach((tab) => this.fadeObserver.observe(tab));
      } else {
        this.onFadeResize = this.queueFade;
        window.addEventListener('resize', this.onFadeResize);
      }
      this.updateFade();
    }

    updateFade() {
      const list = this.tablist;
      if (!list) return;
      const max = list.scrollWidth - list.clientWidth;
      let state = '';
      if (max > 1) {
        const x = Math.abs(list.scrollLeft);
        const more = x < max - 1;
        const less = x > 1;
        if (more && less) state = 'both';
        else if (more) state = 'end';
        else if (less) state = 'start';
      }
      if (state) {
        if (list.getAttribute('data-fade') !== state) list.setAttribute('data-fade', state);
      } else if (list.hasAttribute('data-fade')) {
        list.removeAttribute('data-fade');
      }
    }

    indexFromHash() {
      const id = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';
      if (!id) return -1;
      return this.panels.findIndex((panel) => panel && panel.id === id);
    }

    // Scroll only the tab bar, never the page (used on load and for hash changes).
    revealInList(tab) {
      const list = this.tablist;
      if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
      const pad = 32;
      const listBox = list.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();
      if (tabBox.left < listBox.left + pad) {
        list.scrollLeft += tabBox.left - listBox.left - pad;
      } else if (tabBox.right > listBox.right - pad) {
        list.scrollLeft += tabBox.right - listBox.right + pad;
      }
    }

    select(index, moveFocus, reveal) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (this.panels[i]) this.panels[i].hidden = !active;
      });
      const tab = this.tabs[index];
      if (moveFocus) tab.focus({ preventScroll: true });
      // The tab list has scroll-padding-inline, so the selected tab never sits under the edge fade.
      if (reveal && tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      let next = null;
      switch (event.key) {
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        case ' ':
          event.preventDefault();
          this.select(index, false, true);
          return;
        default:
          return;
      }
      event.preventDefault();
      this.select(next, true, true);
    }
  }

  customElements.define('rgm2-tabs', Rgm2Tabs);
}
